// The station's local database (technical plan §9.2). Engine-neutral: the PowerSync engines
// build their `Schema` from this, and the in-memory fake creates plain SQLite tables from it,
// so every query the UI writes runs the same on both.
//
// Synced tables mirror ENP3 `db/powersync/sync-rules.yaml` (Sync Streams, D-019) column for
// column; `id` is implicit. Postgres types arrive as SQLite text/integer/real: numerics
// (quantities, factors) as text (CONVENTIONS §3: never a float), booleans as 0/1, arrays and
// JSON as JSON text, instants as ISO strings. Keep this file in step with the sync streams.
//
// Local-only tables never sync down and never upload as rows:
//   outbox_command   insert-only: rows exist only in the upload queue, FIFO (plan §9.3)
//   device_state     key → JSON value: lockout, entitlement, config, gates
//   device_sequence  the per-device counter behind device_seq and human numbers (§9.4)
//   command_outcome  final answers a person must see: held (D-024) and rejected commands
//                    (held ones go once the hold is resolved: stream `station_holds`)
//   task_action      what this station did about a daily state-list task (moved, skipped,
//                    written off), so the Expiry list shows it done before the server's
//                    answer syncs back and counts it once the task drops out of the stream
//   batch_action     what this station did about a production batch (started, completed,
//                    cancelled) and the server's answer, so the production queue shows it
//                    honestly before the answer and after the batch leaves the stream
//   receipt_action   a delivery received here and the server's answer, so the Receiving view
//                    shows it (and the order's quantities) honestly before and after the answer
//   handover_action  an order handed over here and the server's answer, so the orders view
//                    shows it honestly before and after the answer

export type ColumnType = 'text' | 'integer' | 'real';

export interface LocalTableDef {
  readonly columns: Readonly<Record<string, ColumnType>>;
  /** Never synced; rows stay on the device. */
  readonly localOnly?: boolean;
  /** Rows are only queued for upload; the table itself stays empty. */
  readonly insertOnly?: boolean;
}

const t = (columns: Record<string, ColumnType>, options: Omit<LocalTableDef, 'columns'> = {}) =>
  ({ columns, ...options }) satisfies LocalTableDef;

/** Tables the sync streams fill (read-only on the station apart from optimistic effects). */
export const SYNCED_TABLES = {
  // stream `branch`
  location: t({ account_id: 'text', branch_id: 'text', type: 'text', name: 'text' }),
  station: t({
    account_id: 'text',
    branch_id: 'text',
    name: 'text',
    profile: 'text',
    hardware_config: 'text',
  }),
  staff_role: t({ account_id: 'text', staff_id: 'text', role_id: 'text', branch_id: 'text' }),
  staff: t({ account_id: 'text', display_name: 'text', pin_hash: 'text', active: 'integer' }),
  // stream `station_devices`
  device: t({
    account_id: 'text',
    station_id: 'text',
    device_code: 'text',
    platform: 'text',
    revoked_at: 'text',
    last_device_seq: 'integer',
    /** A manager unlocked this station's PIN pad from the Admin (D-047, B-080). */
    pin_unlock_requested_at: 'text',
  }),
  // stream `station_holds` (review CR3-5): this station's held commands and where they stand.
  // `id` is the `hold_id` of the `held` answer; a resolved hold prunes the local outcome.
  command_hold: t({
    account_id: 'text',
    command_id: 'text',
    uploaded_by_device_id: 'text',
    reason: 'text',
    status: 'text',
    resolved_at: 'text',
    /** The manager's note: why a held delivery was voided (CR6-102). */
    resolution_note: 'text',
  }),
  // stream `branch_stock`
  stock_balance: t({
    account_id: 'text',
    product_id: 'text',
    lot_id: 'text',
    location_id: 'text',
    state: 'text',
    qty: 'text',
    updated_at: 'text',
  }),
  lot: t({
    account_id: 'text',
    product_id: 'text',
    lot_code: 'text',
    source: 'text',
    produced_at: 'text',
    best_before: 'text',
    supplier_lot_ref: 'text',
    status: 'text',
    verified_at: 'text',
  }),
  // stream `catalogue`
  product: t({
    account_id: 'text',
    name: 'text',
    roles: 'text',
    base_uom: 'text',
    tracks_expiry: 'integer',
    shelf_life_days: 'integer',
    state_rule_id: 'text',
    category_id: 'text',
    deduction_mode: 'text',
    family_id: 'text',
    family_role: 'text',
    standard_cost_cents: 'integer',
  }),
  product_identifier: t({
    account_id: 'text',
    product_id: 'text',
    kind: 'text',
    value: 'text',
    canonical_value: 'text',
  }),
  category: t({ account_id: 'text', name: 'text' }),
  state_rule: t({
    account_id: 'text',
    name: 'text',
    applies_to: 'text',
    category_id: 'text',
    states: 'text',
  }),
  uom_conversion: t({
    account_id: 'text',
    product_id: 'text',
    from_uom: 'text',
    to_uom: 'text',
    factor: 'text',
  }),
  // stream `account`: the account row brings only the business's zone and trading-day
  // cut-off (`HH:mm:ss`, a Postgres time), B-051; `account_id` is its own id.
  account: t({ account_id: 'text', timezone: 'text', trading_day_cutoff: 'text' }),
  account_setting: t({ account_id: 'text', key: 'text', value: 'text' }),
  entitlement: t({
    account_id: 'text',
    module_key: 'text',
    status: 'text',
    valid_until: 'text',
    grace_until: 'text',
  }),
  role: t({ account_id: 'text', name: 'text' }),
  role_permission: t({ account_id: 'text', role_id: 'text', permission_key: 'text' }),
  // stream `stock_control` (M4): the branch's open daily state-list tasks and the approval
  // requests still waiting for a manager. Closed tasks and decided requests drop out.
  state_task: t({
    account_id: 'text',
    branch_id: 'text',
    due_on: 'text',
    first_due_on: 'text',
    product_id: 'text',
    lot_id: 'text',
    location_id: 'text',
    from_state: 'text',
    to_state: 'text',
    qty: 'text',
    kind: 'text',
    overdue: 'integer',
    best_before: 'text',
    rule_name: 'text',
    reason: 'text',
    status: 'text',
  }),
  approval_request: t({
    account_id: 'text',
    branch_id: 'text',
    type: 'text',
    subject_type: 'text',
    subject_id: 'text',
    mode: 'text',
    status: 'text',
    requested_by_staff_id: 'text',
    requested_at: 'text',
    value_cents: 'integer',
  }),
  // stream `production` (M5): the branch's open batches (planned or started), their planned
  // allocations, and the recipes a station needs offline (every active version plus the
  // version each open batch pinned). Stream `production_done` (B-049, subscribed per business
  // day by StationData): the branch's batches completed or cancelled today, whichever station
  // or the office finished them, with the same columns.
  production_batch: t({
    account_id: 'text',
    number: 'text',
    branch_id: 'text',
    product_id: 'text',
    recipe_version_id: 'text',
    status: 'text',
    origin: 'text',
    planned_start: 'text',
    planned_day: 'text',
    planned_qty: 'text',
    station_id: 'text',
    note: 'text',
    started_at: 'text',
    started_by_staff_id: 'text',
    completed_at: 'text',
    completed_day: 'text',
    completed_by_staff_id: 'text',
    actual_yield_qty: 'text',
    output_lot_id: 'text',
    cancelled_at: 'text',
    cancelled_day: 'text',
    cancelled_by_staff_id: 'text',
    cancel_reason: 'text',
    // The order line the batch is planned to make (D-074): the station's own plan check
    // covers a custom item only by it (review CR7-1).
    for_order_line_id: 'text',
  }),
  batch_allocation: t({
    account_id: 'text',
    batch_id: 'text',
    destination: 'text',
    qty: 'text',
    location_id: 'text',
    order_line_id: 'text',
    to_branch_id: 'text',
  }),
  recipe: t({ account_id: 'text', product_id: 'text' }),
  recipe_version: t({
    account_id: 'text',
    recipe_id: 'text',
    version_no: 'integer',
    status: 'text',
    batch_yield_qty: 'text',
    yield_uom: 'text',
    instructions: 'text',
    allergen_statement: 'text',
    signed_off_at: 'text',
  }),
  recipe_line: t({
    account_id: 'text',
    recipe_version_id: 'text',
    position: 'integer',
    component_product_id: 'text',
    qty: 'text',
    uom: 'text',
    loss_pct: 'text',
    allowed_states: 'text',
  }),
  // stream `purchasing` (M6, D-060): the branch's open purchase orders (open, sent, partially
  // received) and their lines, supplier names and supplier items (pack sizes and codes). No
  // prices and no supplier contact details ever reach a station. Stream `receiving_done`
  // (B-054, subscribed per business day by StationData) adds the orders today's deliveries
  // were received against, with the same columns.
  purchase_order: t({
    account_id: 'text',
    number: 'text',
    branch_id: 'text',
    supplier_id: 'text',
    status: 'text',
    expected_date: 'text',
    note: 'text',
  }),
  po_line: t({
    account_id: 'text',
    po_id: 'text',
    line_no: 'integer',
    product_id: 'text',
    supplier_item_id: 'text',
    qty_packs: 'text',
    pack_uom: 'text',
    base_qty_per_pack: 'text',
    ordered_base_qty: 'text',
    received_base_qty: 'text',
  }),
  supplier: t({ account_id: 'text', name: 'text', code: 'text' }),
  // stream `receiving_done` (B-054, subscribed per business day by StationData): the
  // branch's deliveries received today on any of its stations, and their lines. `flags` are
  // JSON arrays of the server's flags; the lot codes come with the `lot` rows. No prices.
  goods_receipt: t({
    account_id: 'text',
    number: 'text',
    branch_id: 'text',
    location_id: 'text',
    po_id: 'text',
    supplier_id: 'text',
    received_at: 'text',
    trading_day: 'text',
    supplier_doc_ref: 'text',
    note: 'text',
    staff_id: 'text',
    flags: 'text',
  }),
  goods_receipt_line: t({
    account_id: 'text',
    receipt_id: 'text',
    line_no: 'integer',
    po_line_id: 'text',
    product_id: 'text',
    supplier_item_id: 'text',
    qty: 'text',
    uom: 'text',
    base_qty: 'text',
    supplier_lot_ref: 'text',
    best_before: 'text',
    lot_id: 'text',
    location_id: 'text',
    state: 'text',
    flags: 'text',
  }),
  supplier_item: t({
    account_id: 'text',
    supplier_id: 'text',
    product_id: 'text',
    supplier_code: 'text',
    supplier_code_canonical: 'text',
    description: 'text',
    pack_uom: 'text',
    base_qty_per_pack: 'text',
    preferred: 'integer',
  }),
  // stream `feasibility` (M7): the branch's current feasibility run and, per open batch, its
  // verdict and flags (`flags`: JSON array of @enp/domain flags in wire shape). A batch row
  // belongs to the current run while its `run_id` is the state's.
  feasibility_state: t({
    account_id: 'text',
    branch_id: 'text',
    run_id: 'text',
    business_day: 'text',
    status: 'text',
    summary: 'text',
    computed_at: 'text',
    // The simulator rules version of the office run (review CR7-101).
    engine_version: 'integer',
  }),
  feasibility_batch_status: t({
    account_id: 'text',
    branch_id: 'text',
    batch_id: 'text',
    run_id: 'text',
    verdict: 'text',
    evaluated: 'integer',
    day: 'text',
    expected_yield: 'text',
    flag_codes: 'text',
    flags: 'text',
    not_evaluated_reasons: 'text',
    computed_at: 'text',
  }),
  // stream `orders_handover` (M7, subscribed per business day by StationData): the branch's
  // orders promised today (confirmed or completed), their live lines, and each line's
  // coverage in the current run. The customer's name is the only personal data; no prices.
  customer_order: t({
    account_id: 'text',
    number: 'text',
    branch_id: 'text',
    kind: 'text',
    customer_name: 'text',
    order_status: 'text',
    fulfilment_status: 'text',
    promised_at: 'text',
    promised_day: 'text',
    note: 'text',
  }),
  order_line: t({
    account_id: 'text',
    order_id: 'text',
    position: 'integer',
    product_id: 'text',
    custom_description: 'text',
    qty: 'text',
    /** How much of the line is handed over already (D-076: the rest is still promised). */
    handed_over_qty: 'text',
    note: 'text',
  }),
  feasibility_line_status: t({
    account_id: 'text',
    branch_id: 'text',
    order_id: 'text',
    order_line_id: 'text',
    run_id: 'text',
    status: 'text',
    evaluated: 'integer',
    at_risk: 'integer',
    day: 'text',
    covered_qty: 'text',
    missing_qty: 'text',
    batch_numbers: 'text',
    reason: 'text',
    message: 'text',
    computed_at: 'text',
  }),
  // stream `feasibility_inputs` (WP7.3): what the station's own feasibility run needs beyond
  // the other streams: open reservations, and the branch's open orders of every day as their
  // own tables (no names, notes or words; ENP3 `customer_order AS planned_order`).
  reservation: t({
    account_id: 'text',
    product_id: 'text',
    lot_id: 'text',
    location_id: 'text',
    qty: 'text',
    order_line_id: 'text',
    status: 'text',
  }),
  planned_order: t({
    account_id: 'text',
    number: 'text',
    branch_id: 'text',
    order_status: 'text',
    fulfilment_status: 'text',
    promised_at: 'text',
  }),
  planned_order_line: t({
    account_id: 'text',
    order_id: 'text',
    position: 'integer',
    product_id: 'text',
    qty: 'text',
    handed_over_qty: 'text',
  }),
} as const;

/** Tables that exist only on the station. */
export const LOCAL_ONLY_TABLES = {
  /** One row per command, `id` = command_id. Insert-only: the upload queue is the outbox. */
  outbox_command: t(
    {
      type: 'text',
      schema_version: 'integer',
      /** JSON text. */
      payload: 'text',
      /** The device that made it (kept across re-pairing, D-024). */
      device_id: 'text',
      staff_id: 'text',
      device_seq: 'integer',
      occurred_at: 'text',
      app_version: 'text',
    },
    { insertOnly: true },
  ),
  /** `id` = key; `value` = JSON. */
  device_state: t({ value: 'text', updated_at: 'text' }, { localOnly: true }),
  /** `id` = device_id. `last_seq` is the last number handed out on this device. */
  device_sequence: t({ device_code: 'text', last_seq: 'integer' }, { localOnly: true }),
  /** `id` = command_id. Final answers a person must see (held, rejected). */
  command_outcome: t(
    {
      type: 'text',
      status: 'text',
      reason: 'text',
      hold_id: 'text',
      message: 'text',
      device_seq: 'integer',
      occurred_at: 'text',
      staff_id: 'text',
      recorded_at: 'text',
      /** Rejected outcomes: when someone at the station tapped OK on the notice. */
      acknowledged_at: 'text',
    },
    { localOnly: true },
  ),
  /**
   * `id` = state_task_id. What this station did about a daily state-list task, written in
   * the same transaction as the command (`moved`, `skipped`, `written_off`).
   */
  task_action: t(
    {
      action: 'text',
      command_id: 'text',
      staff_id: 'text',
      at: 'text',
      /** The business's trading day it was done on (`YYYY-MM-DD`, its zone and cut-off). */
      day: 'text',
    },
    { localOnly: true },
  ),
  /**
   * `id` = command_id. What this station did about a production batch (`start`,
   * `complete`, `cancel`), written in the command's own transaction, and the server's final
   * answer once it comes (D-024). Completed and cancelled batches leave the `production`
   * stream, so the row keeps what the queue needs to show them (./production.ts).
   */
  batch_action: t(
    {
      batch_id: 'text',
      action: 'text',
      staff_id: 'text',
      at: 'text',
      /** The business's trading day it was done on (`YYYY-MM-DD`, its zone and cut-off). */
      day: 'text',
      /** JSON: the batch as the station knew it (number, product, planned quantity, …). */
      batch: 'text',
      /** JSON: what was recorded (yield, output lot, scanned lots, cancel reason). */
      detail: 'text',
      /**
       * `pending` until the server's final answer: `applied`, `applied_with_flags`, `held`,
       * `rejected`; a held one becomes `booked` or `voided` when a manager resolves it.
       */
      status: 'text',
      /** JSON: the answer's `data`, flags, reason and message. */
      answer: 'text',
      answered_at: 'text',
    },
    { localOnly: true },
  ),
  /**
   * `id` = command_id. A delivery received here (`receipt.record`), written in the command's
   * own transaction, and the server's final answer once it comes (D-024). Received orders
   * leave the `purchasing` stream, so the row keeps what the Receiving view shows
   * (./receiving.ts).
   */
  receipt_action: t(
    {
      receipt_id: 'text',
      /** The order received against; null for a delivery without one. */
      po_id: 'text',
      staff_id: 'text',
      at: 'text',
      /** The business day it was received (`YYYY-MM-DD`). */
      day: 'text',
      /** JSON: the receipt as recorded here (number, supplier, lines with their lots, …). */
      detail: 'text',
      /** `pending` until the server's final answer: `applied`, `applied_with_flags`, `held`, `rejected`. */
      status: 'text',
      /** JSON: the answer's `data`, flags, reason and message. */
      answer: 'text',
      answered_at: 'text',
    },
    { localOnly: true },
  ),
  /**
   * `id` = command_id. An order handed over here (`order.handover`), written in the
   * command's own transaction, and the server's final answer once it comes (D-024). A handed
   * over order may leave the day's stream once completed or cancelled in the office, so the
   * row keeps what the orders view shows (./handover.ts).
   */
  handover_action: t(
    {
      order_id: 'text',
      /** `all` or `part`. */
      outcome: 'text',
      note: 'text',
      staff_id: 'text',
      at: 'text',
      /** The business day it was handed over (`YYYY-MM-DD`). */
      day: 'text',
      /** JSON: the order as the station knew it (number, customer, promised time, lines). */
      detail: 'text',
      /** `pending` until the server's final answer: `applied`, `applied_with_flags`, `held`, `rejected`. */
      status: 'text',
      /** JSON: the answer's `data`, flags, reason and message. */
      answer: 'text',
      answered_at: 'text',
    },
    { localOnly: true },
  ),
} as const;

/** Every table of the local database. */
export const LOCAL_SCHEMA = { ...SYNCED_TABLES, ...LOCAL_ONLY_TABLES } as const;

/** A table of the local database. */
export type LocalTableName = keyof typeof LOCAL_SCHEMA;

/** The outbox table (its rows travel as command envelopes). */
export const OUTBOX_TABLE = 'outbox_command' satisfies LocalTableName;
